import { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemText,
  Button,
  TextField,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
} from "@mui/material";
import {
  useGetCompetenciesQuery,
  useCreateCompetencyMutation,
  useUpdateCompetencyMutation,
  useDeleteCompetencyMutation,
  Competency,
} from "../../../entities/admin-competency";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";

export const CompetenciesManagement = () => {
  const [selectedCompetency, setSelectedCompetency] =
    useState<Competency | null>(null);

  // Dialog states
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [competencyName, setCompetencyName] = useState("");

  // Queries and mutations
  const { data: competenciesData, isLoading: isLoadingCompetencies } =
    useGetCompetenciesQuery();

  const [createCompetency, { isLoading: isCreating }] =
    useCreateCompetencyMutation();
  const [updateCompetency, { isLoading: isUpdating }] =
    useUpdateCompetencyMutation();
  const [deleteCompetency, { isLoading: isDeleting }] =
    useDeleteCompetencyMutation();

  // Handlers
  const handleSelectCompetency = (competency: Competency) => {
    setSelectedCompetency(competency);
  };

  const handleOpenCreateDialog = () => {
    setCompetencyName("");
    setOpenCreateDialog(true);
  };

  const handleOpenEditDialog = (competency: Competency) => {
    setSelectedCompetency(competency);
    setCompetencyName(competency.name);
    setOpenEditDialog(true);
  };

  const handleOpenDeleteDialog = (competency: Competency) => {
    setSelectedCompetency(competency);
    setOpenDeleteDialog(true);
  };

  const handleCloseDialogs = () => {
    setOpenCreateDialog(false);
    setOpenEditDialog(false);
    setOpenDeleteDialog(false);
  };

  const handleCreateCompetency = async () => {
    if (!competencyName.trim()) return;

    try {
      await createCompetency({ name: competencyName.trim() }).unwrap();
      handleCloseDialogs();
    } catch (error) {
      console.error("Error creating competency:", error);
    }
  };

  const handleUpdateCompetency = async () => {
    if (!selectedCompetency || !competencyName.trim()) return;

    try {
      await updateCompetency({
        id: selectedCompetency.id,
        body: { name: competencyName.trim() },
      }).unwrap();
      handleCloseDialogs();
    } catch (error) {
      console.error("Error updating competency:", error);
    }
  };

  const handleDeleteCompetency = async () => {
    if (!selectedCompetency) return;

    try {
      await deleteCompetency(selectedCompetency.id).unwrap();
      setSelectedCompetency(null);
      handleCloseDialogs();
    } catch (error) {
      console.error("Error deleting competency:", error);
    }
  };

  useEffect(() => {
    if (competenciesData?.competencies?.length && !selectedCompetency) {
      setSelectedCompetency(competenciesData.competencies[0]);
    }
  }, [competenciesData, selectedCompetency]);

  // Render dialogs
  const renderCreateDialog = () => (
    <Dialog open={openCreateDialog} onClose={handleCloseDialogs}>
      <DialogTitle>Добавить компетенцию</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          margin="dense"
          label="Название компетенции"
          fullWidth
          value={competencyName}
          onChange={(e) => setCompetencyName(e.target.value)}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCloseDialogs}>Отмена</Button>
        <Button
          onClick={handleCreateCompetency}
          disabled={!competencyName.trim() || isCreating}
        >
          {isCreating ? <CircularProgress size={24} /> : "Создать"}
        </Button>
      </DialogActions>
    </Dialog>
  );

  const renderEditDialog = () => (
    <Dialog open={openEditDialog} onClose={handleCloseDialogs}>
      <DialogTitle>Редактировать компетенцию</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          margin="dense"
          label="Название компетенции"
          fullWidth
          value={competencyName}
          onChange={(e) => setCompetencyName(e.target.value)}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCloseDialogs}>Отмена</Button>
        <Button
          onClick={handleUpdateCompetency}
          disabled={!competencyName.trim() || isUpdating}
        >
          {isUpdating ? <CircularProgress size={24} /> : "Сохранить"}
        </Button>
      </DialogActions>
    </Dialog>
  );

  const renderDeleteDialog = () => (
    <Dialog open={openDeleteDialog} onClose={handleCloseDialogs}>
      <DialogTitle>Удалить компетенцию</DialogTitle>
      <DialogContent>
        Вы уверены, что хотите удалить компетенцию "{selectedCompetency?.name}"?
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCloseDialogs}>Отмена</Button>
        <Button
          onClick={handleDeleteCompetency}
          color="error"
          disabled={isDeleting}
        >
          {isDeleting ? <CircularProgress size={24} /> : "Удалить"}
        </Button>
      </DialogActions>
    </Dialog>
  );

  return (
    <Box sx={{ p: 2 }}>
      <Typography fontWeight={600} fontSize={24} mb={2}>
        Управление компетенциями
      </Typography>

      <Box sx={{ display: "flex" }}>
        <Paper sx={{ width: "100%", p: 2 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Typography gutterBottom fontWeight={600} fontSize={18}>
              Компетенции
            </Typography>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={handleOpenCreateDialog}
            >
              Добавить
            </Button>
          </Box>

          {isLoadingCompetencies ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <List>
              {competenciesData?.competencies?.map((competency) => (
                <ListItem
                  key={competency.id}
                  sx={{
                    borderRadius: 1,
                    backgroundColor:
                      selectedCompetency?.id === competency.id
                        ? "#515151"
                        : "inherit",
                    "&:hover": {
                      cursor: "pointer",
                      backgroundColor: "#414141",
                    },
                    mb: 1,
                  }}
                  onClick={() => handleSelectCompetency(competency)}
                  secondaryAction={
                    <Box>
                      <IconButton
                        edge="end"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenEditDialog(competency);
                        }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        edge="end"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenDeleteDialog(competency);
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  }
                >
                  <ListItemText
                    primary={competency.name}
                    slotProps={{ primary: { fontSize: 14 } }}
                  />
                </ListItem>
              ))}
            </List>
          )}
        </Paper>
      </Box>

      {renderCreateDialog()}
      {renderEditDialog()}
      {renderDeleteDialog()}
    </Box>
  );
};
